import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Language } from '../translations';
import { Expedition, Category } from '../types';
import AysenMap from '../components/AysenMap';
import { InstagramFeed } from '../components/InstagramFeed';
import { 
    GitCommit, 
    ArrowRight, 
    Compass, 
    Mountain, 
    ShieldCheck, 
    Radio, 
    Sparkles, 
    Clock, 
    Gauge, 
    MapPin, 
    Sliders,
    ChevronDown,
    Zap,
    Footprints
} from 'lucide-react';

interface HomeBProps {
    t: any;
    lang: Language;
    expeditions?: Expedition[];
    homeSections?: any[];
}

export const HomeB: React.FC<HomeBProps> = ({ t, lang, expeditions = [], homeSections }) => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [activeStation, setActiveStation] = useState<number>(1);
    const [selectedCategory, setSelectedCategory] = useState<Category>('all');
    
    // Interactive route finder state
    const [matcherLevel, setMatcherLevel] = useState<'low' | 'mid' | 'high'>('mid');
    const [matcherDuration, setMatcherDuration] = useState<'day' | 'multi'>('day');

    useEffect(() => {
        const handleScroll = () => {
            const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (totalHeight > 0) {
                const progress = Math.min(Math.max(window.scrollY / totalHeight, 0), 1);
                setScrollProgress(progress);

                // Update active station based on scroll percentage
                if (progress < 0.20) setActiveStation(1);
                else if (progress < 0.45) setActiveStation(2);
                else if (progress < 0.70) setActiveStation(3);
                else if (progress < 0.88) setActiveStation(4);
                else setActiveStation(5);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Filtered expeditions
    const filteredExpeditions = expeditions.filter(exp => {
        if (selectedCategory === 'all') return true;
        return exp.category === selectedCategory;
    }).slice(0, 6);

    // Route finder matched expedition
    const matchedExpedition = expeditions.find(exp => {
        const matchesLevel = exp.difficulty === matcherLevel;
        const matchesDuration = matcherDuration === 'day' 
            ? (exp.duration.includes('Día') || exp.duration.includes('Horas') || exp.duration.includes('Day'))
            : (!exp.duration.includes('1 Día') && (exp.duration.includes('Días') || exp.duration.includes('Days')));
        return matchesLevel && matchesDuration;
    }) || expeditions[0];

    const stations = [
        { id: 1, label: '01 // La Línea', target: 'hero' },
        { id: 2, label: '02 // Los Guías', target: 'filosofia' },
        { id: 3, label: '03 // Las Rutas', target: 'expediciones' },
        { id: 4, label: '04 // El Territorio', target: 'mapa' },
        { id: 5, label: '05 // Tu Ascenso', target: 'cumbre' },
    ];

    return (
        <div className="bg-[#090b0e] text-[#f0f0f0] overflow-x-hidden selection:bg-[#00d4ff] selection:text-black">
            
            {/* =========================================================================
                THE FISSURE SPINE: ACTIVE SVG ROUTE LINE (Fixed Background Guide)
               ========================================================================= */}
            <div className="fixed top-0 bottom-0 left-6 sm:left-12 md:left-24 w-12 z-20 pointer-events-none hidden lg:block">
                <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 50 1000">
                    <defs>
                        <linearGradient id="fissureGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.9" />
                            <stop offset="50%" stopColor="#38bdf8" stopOpacity="1" />
                            <stop offset="100%" stopColor="#00d4ff" stopOpacity="0.8" />
                        </linearGradient>
                        <filter id="fissureGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* Background inactive faint rock groove */}
                    <path
                        d="M 25 0 Q 35 150 20 300 T 32 600 T 18 850 L 25 1000"
                        fill="none"
                        stroke="rgba(255,255,255,0.07)"
                        strokeWidth="3"
                        strokeDasharray="4 4"
                    />

                    {/* Dynamic Active Glowing Crack Line */}
                    <path
                        d="M 25 0 Q 35 150 20 300 T 32 600 T 18 850 L 25 1000"
                        fill="none"
                        stroke="url(#fissureGrad)"
                        strokeWidth="3.5"
                        filter="url(#fissureGlow)"
                        style={{
                            strokeDasharray: 1000,
                            strokeDashoffset: 1000 * (1 - scrollProgress),
                            transition: 'stroke-dashoffset 0.1s linear'
                        }}
                    />
                </svg>

                {/* Progress Head Node (Tracking the tip of the crack) */}
                <div 
                    className="absolute left-[19px] w-3 h-3 rounded-full bg-[#00d4ff] shadow-[0_0_15px_#00d4ff] -translate-x-1/2 -translate-y-1/2 transition-all duration-150"
                    style={{ top: `${Math.max(scrollProgress * 98, 2)}%` }}
                >
                    <span className="absolute inset-0 rounded-full bg-white animate-ping opacity-75" />
                </div>
            </div>

            {/* Station Progress Nav Pill (Floating Left) */}
            <nav 
                aria-label="Navegación por estaciones de montaña"
                className="fixed left-6 top-1/2 -translate-y-1/2 z-30 hidden xl:flex flex-col gap-3 font-mono text-[10px] bg-black/60 backdrop-blur-md p-3 rounded-2xl border border-white/10"
            >
                <div className="text-[#00d4ff] font-bold pb-1 border-b border-white/10 flex items-center gap-1.5">
                    <GitCommit className="w-3.5 h-3.5" />
                    <span>ESTACIONES</span>
                </div>
                {stations.map(st => (
                    <a
                        key={st.id}
                        href={`#${st.target}`}
                        className={`transition-all duration-300 flex items-center gap-2 py-1 px-2 rounded-lg ${
                            activeStation === st.id 
                                ? 'text-white bg-[#00d4ff]/20 font-bold border-l-2 border-[#00d4ff]' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        <span>{st.label}</span>
                    </a>
                ))}
            </nav>


            {/* =========================================================================
                1. HERO SECTION: "LA LÍNEA QUE NADIE MÁS VE" (Estación 01)
               ========================================================================= */}
            <section 
                id="hero"
                className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-black py-20"
            >
                {/* Background Panoramic Imagery */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Cerro%20Colorado/cerro-colorado-mountain-panorama.jpg"
                        alt="Cerro Colorado Fisura Expeditions"
                        className="w-full h-full object-cover scale-105 brightness-[0.65] contrast-[1.1] animate-slow-zoom"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-[#090b0e]" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-black/80" />
                </div>

                {/* Hero Content */}
                <div className="relative z-20 max-w-5xl mx-auto px-6 text-center space-y-8 pt-12 lg:pl-16">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-xs font-mono uppercase tracking-[0.25em] text-emerald-400 backdrop-blur-md">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Propuesta B &bull; La Fisura como Hilo Conductor</span>
                    </div>

                    <div className="space-y-4">
                        <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-title uppercase tracking-tight text-white leading-[0.92] drop-shadow-2xl">
                            Donde otros ven un muro.
                            <br />
                            <span className="bg-gradient-to-r from-white via-cyan-200 to-[#00d4ff] bg-clip-text text-transparent">
                                Nosotros vemos la línea.
                            </span>
                        </h1>
                        <p className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-gray-300 font-light leading-relaxed font-sans">
                            La fisura es el único camino que conecta la base con la cumbre. 
                            Una ruta viva que trazamos paso a paso por el corazón de Aysén.
                        </p>
                    </div>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                        <a
                            href="#expediciones"
                            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#00d4ff] text-black font-title uppercase tracking-widest text-sm font-bold rounded-xl transition-all duration-300 hover:bg-white hover:shadow-[0_0_30px_rgba(0,212,255,0.7)]"
                        >
                            <span>Seguir la Fisura</span>
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </a>

                        <a
                            href="#matcher"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-title uppercase tracking-widest text-sm rounded-xl backdrop-blur-md transition-all duration-300"
                        >
                            <Sliders className="w-4 h-4 text-[#00d4ff]" />
                            <span>Descubre tu Ruta Ideal</span>
                        </a>
                    </div>

                    {/* Scroll Prompt */}
                    <div className="pt-12 flex flex-col items-center gap-2 text-gray-400 text-xs font-mono tracking-widest uppercase">
                        <span>Baja para recorrer la vía</span>
                        <ChevronDown className="w-4 h-4 text-[#00d4ff] animate-bounce" />
                    </div>
                </div>
            </section>


            {/* =========================================================================
                2. ESTACIÓN 02: LOS GUÍAS Y EL ANCLAJE TÉCNICO (Filosofía)
               ========================================================================= */}
            <section id="filosofia" className="relative py-28 px-6 max-w-7xl mx-auto lg:pl-32 space-y-16">
                <div className="space-y-4 max-w-3xl">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.4em] text-[#00d4ff]">
                        <span className="w-8 h-px bg-[#00d4ff]" />
                        <span>Estación 02 // El Anclaje Técnico</span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-title uppercase tracking-tight text-white">
                        La seguridad que sostiene cada paso
                    </h2>
                    <p className="text-gray-300 font-light text-base sm:text-lg leading-relaxed">
                        En la escalada en fisura, la confianza radica en los anclajes que introduces en la roca. 
                        En Fisura Expeditions, nuestros anclajes son la excelencia técnica, la acreditación oficial y el respeto absoluto a la montaña.
                    </p>
                </div>

                {/* Technical Anchors Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="relative p-8 rounded-2xl bg-[#111620] border border-white/10 hover:border-[#00d4ff]/40 transition-all duration-300 space-y-4 group">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-xs text-[#00d4ff]">ANCLAJE 01</span>
                            <ShieldCheck className="w-6 h-6 text-[#00d4ff] group-hover:scale-110 transition-transform" />
                        </div>
                        <h3 className="text-2xl font-title uppercase text-white">Guías ANGM & WFR</h3>
                        <p className="text-sm text-gray-400 font-light leading-relaxed">
                            Formados y acreditados bajo los estándares más rigurosos de Chile. Expertos en rescate agreste y gestión de riesgo en terreno técnico.
                        </p>
                    </div>

                    <div className="relative p-8 rounded-2xl bg-[#111620] border border-white/10 hover:border-[#00d4ff]/40 transition-all duration-300 space-y-4 group">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-xs text-[#00d4ff]">ANCLAJE 02</span>
                            <Radio className="w-6 h-6 text-[#00d4ff] group-hover:scale-110 transition-transform" />
                        </div>
                        <h3 className="text-2xl font-title uppercase text-white">Tecnología Garmin InReach</h3>
                        <p className="text-sm text-gray-400 font-light leading-relaxed">
                            Rastreo satelital activo en cada minuto de tu expedición. Comunicación directa con servicios de emergencia sin depender de señal celular.
                        </p>
                    </div>

                    <div className="relative p-8 rounded-2xl bg-[#111620] border border-white/10 hover:border-[#00d4ff]/40 transition-all duration-300 space-y-4 group">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-xs text-[#00d4ff]">ANCLAJE 03</span>
                            <Footprints className="w-6 h-6 text-[#00d4ff] group-hover:scale-110 transition-transform" />
                        </div>
                        <h3 className="text-2xl font-title uppercase text-white">Cero Huella & Ética</h3>
                        <p className="text-sm text-gray-400 font-light leading-relaxed">
                            Cumplimiento estricto de No Deje Rastro (NDR). Grupos reducidos para proteger la fragilidad del bosque y la roca de Aysén.
                        </p>
                    </div>
                </div>
            </section>


            {/* =========================================================================
                3. ESTACIÓN 03: LAS RUTAS ABIERTAS (Expediciones Conectadas)
               ========================================================================= */}
            <section id="expediciones" className="relative py-28 px-6 max-w-7xl mx-auto lg:pl-32 space-y-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.4em] text-[#00d4ff]">
                            <span className="w-8 h-px bg-[#00d4ff]" />
                            <span>Estación 03 // Las Rutas Abiertas</span>
                        </div>
                        <h2 className="text-3xl sm:text-5xl font-title uppercase tracking-tight text-white">
                            Los Largos de Cuerda: Elige tu Salida
                        </h2>
                    </div>

                    {/* Filter Buttons */}
                    <div className="flex flex-wrap gap-2">
                        {[
                            { id: 'all', label: 'Todo' },
                            { id: 'trekking', label: 'Trekking' },
                            { id: 'climbing', label: 'Escalada' },
                            { id: 'water', label: 'Agua' },
                        ].map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id as Category)}
                                className={`px-4 py-2 rounded-xl text-xs font-title uppercase tracking-wider transition-all duration-300 ${
                                    selectedCategory === cat.id
                                        ? 'bg-[#00d4ff] text-black font-bold shadow-[0_0_15px_rgba(0,212,255,0.4)]'
                                        : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Timeline / Route Branch Cards */}
                <div className="space-y-6">
                    {filteredExpeditions.map((exp, idx) => {
                        const name = exp[`name_${lang}`] || exp.name_es || exp.name_en;
                        const catchphrase = exp[`catchphrase_${lang}`] || exp.catchphrase_es || '';
                        const price = exp.price_from || exp.price_flat;

                        return (
                            <Link
                                key={exp.id || idx}
                                to={`/expeditions/${exp.slug}`}
                                className="group relative block p-6 sm:p-8 rounded-2xl bg-[#111620] border border-white/10 hover:border-[#00d4ff] transition-all duration-500 hover:shadow-[0_10px_40px_rgba(0,212,255,0.15)]"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                                    {/* Number / Route Marker */}
                                    <div className="md:col-span-1 hidden md:flex flex-col items-center">
                                        <span className="font-mono text-2xl font-bold text-gray-600 group-hover:text-[#00d4ff] transition-colors">
                                            0{idx + 1}
                                        </span>
                                        <div className="w-px h-8 bg-white/10 mt-2" />
                                    </div>

                                    {/* Thumbnail */}
                                    <div className="md:col-span-3 rounded-xl overflow-hidden h-44 w-full relative">
                                        <img
                                            src={exp.image}
                                            alt={name}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-black/70 backdrop-blur-md text-[10px] font-mono text-[#00d4ff] uppercase">
                                            {exp.category}
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="md:col-span-5 space-y-2">
                                        <h3 className="text-2xl font-title uppercase tracking-wide text-white group-hover:text-[#00d4ff] transition-colors">
                                            {name}
                                        </h3>
                                        {catchphrase && (
                                            <p className="text-sm text-gray-400 font-light line-clamp-2">
                                                "{catchphrase}"
                                            </p>
                                        )}
                                        <div className="flex items-center gap-4 pt-2 text-xs font-mono text-gray-400">
                                            <span className="flex items-center gap-1.5">
                                                <Clock className="w-3.5 h-3.5 text-[#00d4ff]" />
                                                {exp.duration}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <Gauge className="w-3.5 h-3.5 text-[#00d4ff]" />
                                                {exp.difficulty === 'high' ? 'Exigente' : exp.difficulty === 'mid' ? 'Media-Alta' : 'Accesible'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Price & CTA */}
                                    <div className="md:col-span-3 flex md:flex-col items-center md:items-end justify-between gap-2 pt-4 md:pt-0 border-t md:border-t-0 border-white/10">
                                        <div className="text-left md:text-right">
                                            <span className="text-[10px] font-mono text-gray-400 uppercase block">Desde</span>
                                            <span className="text-2xl font-title font-bold text-white group-hover:text-[#00d4ff] transition-colors">
                                                {price ? `$${price} USD` : 'Consultar'}
                                            </span>
                                        </div>

                                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 group-hover:bg-[#00d4ff] group-hover:text-black font-title uppercase tracking-wider text-xs font-bold transition-all">
                                            <span>Ver Ficha</span>
                                            <ArrowRight className="w-3.5 h-3.5" />
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>


            {/* =========================================================================
                INTERACTIVE WIDGET: "DESCUBRE TU RUTA IDEAL" (Fissure Route Matcher)
               ========================================================================= */}
            <section id="matcher" className="py-20 px-6 max-w-5xl mx-auto lg:pl-32">
                <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-[#121824] to-[#0c1017] border border-[#00d4ff]/30 shadow-[0_20px_60px_rgba(0,0,0,0.8)] space-y-8">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#00d4ff]">
                        <Compass className="w-4 h-4" />
                        <span>Interactive Matcher &bull; Recomendador Inteligente</span>
                    </div>

                    <div className="space-y-2">
                        <h2 className="text-3xl sm:text-4xl font-title uppercase text-white">
                            ¿Cuál es la fisura adecuada para ti?
                        </h2>
                        <p className="text-gray-400 font-light text-sm sm:text-base">
                            Selecciona tus parámetros para encontrar la experiencia que mejor conecta con tu ritmo.
                        </p>
                    </div>

                    {/* Selectors */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                        {/* Selector 1: Nivel de exigencia */}
                        <div className="space-y-2">
                            <label className="text-xs font-mono uppercase tracking-wider text-gray-300 block">
                                Nivel de Desafío Físico:
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { id: 'low', label: 'Accesible' },
                                    { id: 'mid', label: 'Intermedio' },
                                    { id: 'high', label: 'Exigente' },
                                ].map((lvl) => (
                                    <button
                                        key={lvl.id}
                                        onClick={() => setMatcherLevel(lvl.id as any)}
                                        className={`py-2.5 px-2 text-center rounded-xl text-xs font-title uppercase tracking-wider transition-all ${
                                            matcherLevel === lvl.id
                                                ? 'bg-[#00d4ff] text-black font-bold shadow-md'
                                                : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
                                        }`}
                                    >
                                        {lvl.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Selector 2: Duración */}
                        <div className="space-y-2">
                            <label className="text-xs font-mono uppercase tracking-wider text-gray-300 block">
                                Tiempo Disponible:
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { id: 'day', label: '1 Día / Full Day' },
                                    { id: 'multi', label: 'Travesía (Varios Días)' },
                                ].map((dur) => (
                                    <button
                                        key={dur.id}
                                        onClick={() => setMatcherDuration(dur.id as any)}
                                        className={`py-2.5 px-3 text-center rounded-xl text-xs font-title uppercase tracking-wider transition-all ${
                                            matcherDuration === dur.id
                                                ? 'bg-[#00d4ff] text-black font-bold shadow-md'
                                                : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
                                        }`}
                                    >
                                        {dur.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Matched Result Card */}
                    {matchedExpedition && (
                        <div className="p-6 rounded-2xl bg-black/50 border border-white/15 flex flex-col sm:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <img
                                    src={matchedExpedition.image}
                                    alt={matchedExpedition.name_es}
                                    className="w-16 h-16 rounded-xl object-cover"
                                />
                                <div>
                                    <span className="text-[10px] font-mono uppercase text-[#00d4ff] block">Recomendación Sugerida</span>
                                    <h4 className="text-xl font-title uppercase text-white">
                                        {matchedExpedition.name_es}
                                    </h4>
                                    <span className="text-xs text-gray-400 font-mono">
                                        {matchedExpedition.duration} &bull; {matchedExpedition.difficulty === 'high' ? 'Exigente' : matchedExpedition.difficulty === 'mid' ? 'Media-Alta' : 'Accesible'}
                                    </span>
                                </div>
                            </div>

                            <Link
                                to={`/expeditions/${matchedExpedition.slug}`}
                                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-[#00d4ff] hover:bg-white text-black font-title uppercase tracking-widest text-xs font-bold transition-all text-center"
                            >
                                Ver Esta Expedición
                            </Link>
                        </div>
                    )}
                </div>
            </section>


            {/* =========================================================================
                4. ESTACIÓN 04: EL TERRITORIO Y CARTOGRAFÍA VIVA (AysenMap)
               ========================================================================= */}
            <section id="mapa" className="py-24 px-6 max-w-7xl mx-auto lg:pl-32 space-y-8">
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.4em] text-[#00d4ff]">
                        <span className="w-8 h-px bg-[#00d4ff]" />
                        <span>Estación 04 // El Territorio</span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-title uppercase tracking-tight text-white">
                        La Cuenca de Cerro Castillo en Vivo
                    </h2>
                </div>

                <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <AysenMap expeditions={expeditions} />
                </div>
            </section>


            {/* =========================================================================
                5. ESTACIÓN 05: LA CUMBRE & CIERRE (Call to Action)
               ========================================================================= */}
            <section id="cumbre" className="py-32 px-6 max-w-5xl mx-auto lg:pl-32 text-center space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00d4ff]/10 border border-[#00d4ff]/30 text-xs font-mono uppercase tracking-[0.3em] text-[#00d4ff]">
                    <Mountain className="w-4 h-4" />
                    <span>Estación 05 // La Cumbre</span>
                </div>

                <h2 className="text-4xl sm:text-6xl font-title uppercase tracking-tight text-white max-w-3xl mx-auto">
                    ¿Listo para entrar en la fisura?
                </h2>

                <p className="max-w-xl mx-auto text-gray-300 font-light text-base sm:text-lg">
                    Operamos de Octubre a Abril en Villa Cerro Castillo. Los cupos por expedición son limitados para preservar la seguridad técnica y la intimidad del grupo.
                </p>

                <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link
                        to="/expeditions"
                        className="px-10 py-4 rounded-xl bg-[#00d4ff] hover:bg-white text-black font-title uppercase tracking-widest text-sm font-bold transition-all shadow-[0_0_30px_rgba(0,212,255,0.5)]"
                    >
                        Reservar Expedición
                    </Link>
                    <Link
                        to="/about"
                        className="px-10 py-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-title uppercase tracking-widest text-sm transition-all"
                    >
                        Conocer a los Guías
                    </Link>
                </div>
            </section>


            {/* =========================================================================
                INSTAGRAM FEED
               ========================================================================= */}
            <section className="py-20 bg-[#06080a] border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:pl-32 space-y-12">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <span className="text-[10px] font-mono uppercase tracking-[0.4em] text-[#00d4ff] block mb-2">
                                Señal de Cumbre
                            </span>
                            <h3 className="text-2xl sm:text-3xl font-title uppercase tracking-tight text-white">
                                Feed de Instagram
                            </h3>
                        </div>
                        <a
                            href="https://www.instagram.com/fisuraexpeditions/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono text-gray-400 hover:text-[#00d4ff] uppercase tracking-widest border-b border-white/20 pb-1 transition-colors"
                        >
                            @fisuraexpeditions
                        </a>
                    </div>
                    <InstagramFeed />
                </div>
            </section>
        </div>
    );
};

export default HomeB;
